import {useEffect, useState, type ComponentType, type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {FormProvider, useForm} from 'react-hook-form';
import {CreditCard, Lock, TriangleAlert} from 'lucide-react';
import {useElements} from '@stripe/react-stripe-js';
import {PAYMENT_PROVIDERS, PaymentsCart} from '@chekin/core';
import {Button, cn, useEvent} from '@chekinapp/ui';
import {StatusModalStates} from '../../../components';
import {checkIfTotalAmountToPayZero} from '../cartUtils';
import {formatMoney} from '../money';
import {getHoldSummary, getPayAction} from '../paymentKinds';
import {buildPaymentTimeline, getFailureReason} from '../paymentTimeline';
import {useBuildTotalPayments} from '../hooks/useBuildTotalPayments';
import {usePayment} from '../hooks/usePayment';
import {usePaymentFlowPhase} from '../hooks/usePaymentFlowPhase';
import {usePaymentProcessingState} from '../hooks/usePaymentProcessingState';
import {usePaymentSettings} from '../hooks/usePaymentSettings';
import {PaymentFormSkeleton} from './PaymentFormSkeleton';
import {StripePaymentForm} from './StripePaymentForm';
import {PaymentAmounts} from './flow/PaymentAmounts';
import {getCardFaceState} from './flow/cardFaceState';
import {PaymentCardFace} from './flow/PaymentCardFace';
import {PaymentFlowShell} from './flow/PaymentFlowShell';
import {PaymentProgressActions, PaymentProgressBody} from './flow/PaymentProgress';

const PAYMENT_FORM_ID = 'payment-form';

type ProviderFormProps = {
  onFormReady?: () => void;
  onFormComplete?: (state: boolean) => void;
  onFormDirty?: (state: boolean) => void;
};

const ProviderForms: Partial<
  Record<PAYMENT_PROVIDERS, ComponentType<ProviderFormProps>>
> = {
  [PAYMENT_PROVIDERS.stripe]: StripePaymentForm,
};

type PaymentFormState = {
  isDirty: boolean;
  processState: StatusModalStates;
};

type Props = {
  isLoading: boolean;
  onSuccess: () => void;
  paymentsCart?: PaymentsCart;
  successActions?: ReactNode;
  onReceiptsReady?: () => void;
  onStateChange?: (state: PaymentFormState) => void;
};

function PaymentForm({
  paymentsCart,
  isLoading,
  onSuccess,
  successActions,
  onReceiptsReady,
  onStateChange,
}: Props) {
  const {t} = useTranslation();
  const elements = useElements();
  const [isFormReady, setIsFormReady] = useState(false);
  const [isFormComplete, setIsFormComplete] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [retryNote, setRetryNote] = useState<'retry' | 'other'>();
  const {paymentProvider, currencySymbol} = usePaymentSettings();
  const methods = useForm({mode: 'onTouched'});
  const {handleSubmit} = methods;
  const {totalToPayNow} = useBuildTotalPayments({paymentsCart});
  const {heldTotal, isAuthorisation} = getHoldSummary(paymentsCart);
  const {
    processState,
    progress,
    isBankConfirming,
    failureCode,
    onProgress,
    onBankConfirmation,
    onPaymentSuccess,
    onPaymentError,
    setProcessState,
    closeProcessingModal,
  } = usePaymentProcessingState({onSuccess, autoDismissSuccess: false});

  const payments = paymentsCart?.pay_now_payments;
  const isPaymentsAmountZero = checkIfTotalAmountToPayZero(paymentsCart);
  const isPayProcessDisabled =
    !isFormReady ||
    !isFormComplete ||
    !payments?.length ||
    isPaymentsAmountZero ||
    processState !== StatusModalStates.IDLE;

  const {pay} = usePayment({
    onPaymentExpired: closeProcessingModal,
    onPaymentSuccess,
    onPaymentError,
    onProgress,
    onBankConfirmation,
  });

  const handleStateChange = useEvent(onStateChange);
  useEffect(() => {
    handleStateChange({isDirty: isFormDirty, processState});
  }, [handleStateChange, isFormDirty, processState]);

  const [submitted, setSubmitted] = useState<{cart?: PaymentsCart; total: number}>();
  const chargeTotal = Number(totalToPayNow) || 0;
  const {phase, isPaying} = usePaymentFlowPhase({
    processState,
    hasSuccessActions: Boolean(successActions),
    onReceiptsReady,
  });

  const ProviderForm = ProviderForms[paymentProvider];

  const onSubmit = () => {
    if (isPayProcessDisabled) return;
    setSubmitted({cart: paymentsCart, total: chargeTotal});
    setRetryNote(undefined);
    setProcessState(StatusModalStates.LOADING);
    pay({payments});
  };

  const backToForm = (note: 'retry' | 'other') => {
    if (note === 'other') elements?.getElement('payment')?.clear();
    setRetryNote(note);
    closeProcessingModal();
  };

  const rows = buildPaymentTimeline({
    paymentsCart: submitted?.cart ?? paymentsCart,
    totalToPayNow: submitted?.total ?? chargeTotal,
    multiPayment: progress,
    hasBankStep: true,
    isBankConfirming,
    isFailed: phase === 'error',
  });

  const cardState = getCardFaceState(phase, {
    isVerifying: rows.some(row => row.key === 'bank' && row.state === 'on'),
    isFormComplete,
  });

  const {amountLabel, label: payLabel} = getPayAction(t, {
    chargeTotal,
    heldTotal,
    isAuthorisation,
    currencySymbol,
  });

  return (
    <PaymentFlowShell
      title={t(isPaying ? 'pay_flow.your_payment' : 'pay_flow.add_card')}
      isPaying={isPaying}
      card={<PaymentCardFace state={cardState} isCompact={isPaying} />}
      amounts={
        <PaymentAmounts
          chargeTotal={chargeTotal}
          holdTotal={heldTotal}
          isAuthorisation={isAuthorisation}
          currencySymbol={currencySymbol}
        />
      }
      footer={
        phase ? (
          <PaymentProgressActions
            phase={phase}
            onTryAgain={() => backToForm('retry')}
            onUseAnotherCard={() => backToForm('other')}
            successActions={successActions}
          />
        ) : (
          <>
            <Button
              type="submit"
              form={PAYMENT_FORM_ID}
              disabled={isPayProcessDisabled}
              loading={isLoading}
              className="w-full gap-2 [&_svg]:size-4"
            >
              <Lock />
              {payLabel}
            </Button>
            <p className="text-center text-[12px] leading-4 text-[var(--chekin-text-secondary)]">
              {t('pay_flow.secured_by_stripe')}
            </p>
          </>
        )
      }
    >
      {phase && (
        <PaymentProgressBody
          phase={phase}
          rows={rows}
          isBankConfirming={isBankConfirming}
          reason={phase === 'error' ? getFailureReason(progress, failureCode) : undefined}
          amountLabel={amountLabel}
          currencySymbol={currencySymbol}
        />
      )}

      <div
        className={cn('flex flex-col gap-3', isPaying && 'hidden')}
        aria-hidden={isPaying || undefined}
      >
        {retryNote && (
          <p
            role="status"
            className="flex items-start gap-2 rounded-[12px] bg-[var(--chekin-red-50)] px-3 py-2.5 text-[13px] leading-[18px] text-[var(--chekin-color-brand-red)]"
          >
            <TriangleAlert className="mt-px size-4 shrink-0" />
            {t(
              retryNote === 'retry' ? 'pay_flow.retry_note' : 'pay_flow.other_card_note',
            )}
          </p>
        )}
        <FormProvider {...methods}>
          <form id={PAYMENT_FORM_ID} onSubmit={handleSubmit(onSubmit)}>
            {ProviderForm && (
              <>
                {!isFormReady && <PaymentFormSkeleton />}
                <ProviderForm
                  onFormReady={() => setIsFormReady(true)}
                  onFormComplete={setIsFormComplete}
                  onFormDirty={setIsFormDirty}
                />
              </>
            )}
          </form>
        </FormProvider>
        {heldTotal > 0 && (
          <p className="flex items-start gap-2 text-[13px] leading-[18px] text-[var(--chekin-text-secondary)]">
            <CreditCard className="mt-px size-4 shrink-0 text-[var(--chekin-color-brand-blue)]" />
            {t(
              isAuthorisation
                ? 'pay_flow.same_card_authorise'
                : 'pay_flow.same_card_hold',
              {
                amount: formatMoney(heldTotal, currencySymbol),
              },
            )}
          </p>
        )}
      </div>
    </PaymentFlowShell>
  );
}

export {PaymentForm};
export type {PaymentFormState};
